import { CommonLanguageBridge } from '@lg/shared'
import {
	BehaviorSubject,
	combineLatest,
	distinctUntilChanged,
	filter,
	map,
	shareReplay,
	switchMap,
} from 'rxjs'
import { AppContext } from '../app-context.js'
import { IdbService } from '../services/idb.service.js'
import { LanguageInfo } from '@lg/shared'
import enUS from 'date-fns/locale/en-US'
import { signal } from '@angular/core'

export const IdbLanguageKey = 'language'
export const IdbEditModeKey = 'editMode'

export class FrontendLanguageBridge extends CommonLanguageBridge {
	protected static languageSubject = new BehaviorSubject<string | undefined>(
		undefined,
	)
	static readonly effectiveLanguage$ = combineLatest([
		this.languageSubject,
		this.defaultLanguage$,
	]).pipe(
		map(([language, defaultLanguage]) => {
			const effectiveLanguage = language || defaultLanguage
			return effectiveLanguage
		}),
		shareReplay(),
	)
	static readonly effectiveLanguage = signal(this.defaultLanguageSubject.value)
	private static effectiveLanguageSubscription =
		this.effectiveLanguage$.subscribe((effectiveLanguage) => {
			this.effectiveLanguage.set(effectiveLanguage)
		})
	static readonly languageChanged$ = this.languageChangedSubject
		.asObservable()
		.pipe(
			switchMap(() => this.effectiveLanguage$),
			filter(Boolean),
			distinctUntilChanged(),
			shareReplay(),
		)
	protected static readonly editModeSignal = signal(false)
	private static languagesCache: [string, LanguageInfo][] | undefined

	static get languages() {
		if (!this.languagesCache) {
			this.languagesCache = Object.entries(AppContext.languages)
		}
		return this.languagesCache!
	}

	static get language(): string {
		return this.effectiveLanguage()
	}
	static set language(language: string | undefined) {
		AppContext.resolve(IdbService).put(IdbLanguageKey, language)
		this.languageSubject.next(language)
		this.languageChangedSubject.next()
	}
	static get languageIsoCode() {
		return AppContext.languages[this.language]?.isoCode ?? this.language
	}

	static get editMode() {
		return this.editModeSignal()
	}
	static set editMode(editMode: boolean) {
		AppContext.resolve(IdbService).put(IdbEditModeKey, editMode)
		if (editMode == this.editModeSignal()) return
		this.editModeSignal.set(editMode)
		this.languageChangedSubject.next()
	}

	static init() {
		setTimeout(async () => {
			this.language =
				await AppContext.resolve(IdbService).get<string>(IdbLanguageKey)
			this.editMode =
				(await AppContext.resolve(IdbService).get<boolean>(IdbEditModeKey)) ??
				false
		})
	}
}

FrontendLanguageBridge.θsetDateLocales({ enUS })
